import React, { useRef } from 'react';
import { useAtomValue } from 'jotai/index';
import { ReactComponent as EyeIcon } from '@rocco/icons/svg/eye.svg';
import { ReactComponent as LikeIcon } from '@rocco/icons/svg/like.svg';
import { shortenCount } from '@rocco/utils/format-number';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { ProjectTileCounterBadge } from './ProjectTileCounterBadge';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';

import { useTracker } from '../../../../../tracker';
import {
    trackTileMediaCarouselNext,
    trackTileMediaDetailClick,
} from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { useABTestIsOn } from '@archipro-website/web/app/modules/root/hook/use-growthbook';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

//Tracker for AB Test is Just for the AB Test purpose and should be removed after we realise our result

const PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD = 1;
const PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD = 100;

export const ProjectTile = (props: TileViewModel<ProjectTileViewModel>) => {
    const {
        aspectRatio,
        model: project,
        section,
        isFollowed,
        onFollow,
        routeState,
        hideFollowButton = false,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
    } = props;
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const isTileMediaSlider = useABTestIsOn('tile-media-slider');
    const tracker = useTracker();

    const mediaTileStyle = cn(
        'group relative',
        aspectRatio === 'featured' ? 'h-full' : undefined
    );

    const handleTileClick = () => {
        onClick?.();
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            project.id,
            project.professional.id,
            section
        );
        trackTileMediaDetailClick(tracker, {
            itemID: project.id,
            itemType: 'ProjectTile',
            itemName: project.title,
            variant: isTileMediaSlider ? 'A' : 'B',
        });

        tileActions?.trackingConfig?.onViewMoreTrack?.({
            variant: isTileMediaSlider ? 'A' : 'B',
        });
    };

    const tileContent = (
        <div className={cn('flex flex-col gap-4', className)}>
            <div className={mediaTileStyle}>
                <TileMedia
                    aspectRatio={aspectRatio}
                    images={project.images}
                    video={project.video}
                    onSlideChange={(
                        direction: 'CarouselPrev' | 'CarouselNext',
                        currentIndex: number
                    ) => {
                        if (
                            direction === 'CarouselNext' &&
                            currentIndex === 4
                        ) {
                            console.info(
                                'isTileMediaSlider',
                                isTileMediaSlider ? 'A' : 'B'
                            );

                            trackTileMediaCarouselNext(tracker, {
                                itemID: project.id,
                                itemType: 'ProjectTile',
                                itemName: project.title,
                                variant: isTileMediaSlider ? 'A' : 'B',
                            });
                        }
                        tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                            direction,
                            project.id
                        );
                    }}
                />

                <div className="absolute inset-0 hidden rounded-tile bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" />
                {project.badge && (
                    <div className="absolute left-4 top-3">
                        <TileBadge title={project.badge} />
                    </div>
                )}

                <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                    {!hideFollowButton && (
                        <TileFollowButton
                            pinId={project.id}
                            pinType="ProjectPin"
                            isFollowed={isFollowed}
                            onFollow={onFollow}
                        />
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {inlineActionButtons && (
                    <div className="flex flex-col">{inlineActionButtons}</div>
                )}
                <div
                    className={`${TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK} flex flex-col gap-[0.125rem]`}
                >
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] md:text-base md:tracking-[0.02rem]">
                        {project.title}
                    </span>
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:text-base md:tracking-[0.02rem]">
                        {project.professional.name}
                    </span>
                </div>

                <div className="flex justify-start gap-[0.5625rem] empty:hidden md:pb-[0.325rem]">
                    {!!project.viewedCount &&
                        project.viewedCount >
                            PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD && (
                            <ProjectTileCounterBadge
                                icon={EyeIcon}
                                value={shortenCount(project.viewedCount ?? 0)}
                            />
                        )}
                    {!!project.savedCount &&
                        project.savedCount >
                            PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD && (
                            <ProjectTileCounterBadge
                                icon={LikeIcon}
                                value={shortenCount(project.savedCount ?? 0)}
                            />
                        )}
                </div>
            </div>
        </div>
    );

    const linkProps = disableLink
        ? {
              mode: 'override' as const,
          }
        : {
              mode: 'normal' as const,
              prefetch: 'intent' as const,
          };

    return (
        <OptionalLink
            to={project.link}
            state={routeState}
            onClick={handleTileClick}
            ref={tileRef}
            {...linkProps}
        >
            {tileContent}
        </OptionalLink>
    );
};
